import React, { Component, createRef } from "react";
import { connect } from "react-redux";

import { displayFlashMessage } from "Actions";
import { saveCommunityDrawersSettings } from "Thunks";
import { getCommunityDrawersSettings, isUrl } from "Utils";
import { selectRouting } from "Selectors";

import _ from "i18n";

import styles from "./ManageDrawers.local.scss";

export class ManageDrawers extends Component {
  constructor(props) {
    super(props);

    const { navCommunity } = props;
    const drawersSettings = getCommunityDrawersSettings(navCommunity);

    this.state = {
      drawers: drawersSettings,
      invalidDrawers: [],
      newDrawer: null,
      newDrawerError: null
    };

    this.formRef = createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { navCommunity } = this.props;

    if (prevProps.navCommunity !== navCommunity) {
      const drawersSettings = getCommunityDrawersSettings(navCommunity);
      this.setState({ drawers: drawersSettings, invalidDrawers: [] });
    }

    if (!prevState.newDrawer && this.state.newDrawer && this.formRef.current) {
      this.formRef.current.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth"
      });
    }
  }

  save = () => {
    const { drawers } = this.state;
    const { saveCommunityDrawersSettings, displayFlashMessage } = this.props;

    const invalidDrawers = [];
    drawers.forEach((d, index) => {
      if (!d.url.trim() || !isUrl(d.url)) {
        invalidDrawers.push(index);
      }
    });

    if (invalidDrawers.length > 0) {
      displayFlashMessage("ERROR", _("Invalid URL"));
      return this.setState({
        invalidDrawers
      });
    }

    this.setState({
      invalidDrawers: []
    });

    saveCommunityDrawersSettings({ drawers })
      .then(() => {
        displayFlashMessage("SUCCESS", _("Settings changed successfully"));
      })
      .catch(() => {
        displayFlashMessage("ERROR", _("Something went wrong"));
      });
  };

  createDrawer = () => {
    const { newDrawer } = this.state;

    if (newDrawer) {
      return;
    }

    this.setState({
      newDrawer: {
        app: "OTHER",
        label: "",
        url: "",
        enabled: true
      }
    });
  };

  addNewDrawer = () => {
    const { drawers, newDrawer } = this.state;

    if (!newDrawer) {
      return;
    }

    if (!newDrawer.label.trim() || !newDrawer.url.trim()) {
      return this.setState({
        newDrawerError: "All fields are required"
      });
    }

    if (!isUrl(newDrawer.url)) {
      return this.setState({
        newDrawerError: "Invalid URL"
      });
    }

    this.setState({
      drawers: [...drawers, newDrawer],
      newDrawer: null,
      newDrawerError: null
    });
  };

  cancel = () => {
    const { navCommunity } = this.props;

    const drawersSettings = getCommunityDrawersSettings(navCommunity);
    this.setState({
      drawers: drawersSettings,
      invalidDrawers: [],
      newDrawer: null,
      newDrawerError: null
    });
  };

  handleUrlChange = ({ target }, index) => {
    this.setState(({ drawers }) => {
      const newDrawers = drawers.map((d, i) => {
        if (i !== index) {
          return d;
        }

        return {
          ...d,
          url: target.value
        };
      });

      return { drawers: newDrawers };
    });
  };

  handleEnabledChange = index => {
    this.setState(({ drawers }) => {
      const newDrawers = drawers.map((d, i) => {
        if (i !== index) {
          return d;
        }

        return {
          ...d,
          enabled: !d.enabled
        };
      });

      return { drawers: newDrawers };
    });
  };

  deleteDrawer = index => {
    this.setState(({ drawers }) => {
      const newDrawers = drawers.filter((_, i) => index !== i);

      return { drawers: newDrawers };
    });
  };

  renderHeader() {
    return (
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <img src="/img/icons/settings.svg" alt="settings" />
          </div>
          <div className={styles.headerInfo}>
            <div>
              <h3>{_("Drawers")}</h3>
              <p>{_("Manage the drawers that appear on your portal")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderActions({ border = false } = {}) {
    const { newDrawer } = this.state;
    const { saving } = this.props;

    return (
      <div
        className={`grid-x ${styles.actions}`}
        style={border ? { borderBottom: "1px solid #F1F2F4" } : {}}
      >
        <button className="controls__cancel" onClick={this.cancel}>
          {_("Cancel")}
        </button>
        <button
          className="controls__cancel"
          disabled={!!newDrawer}
          onClick={this.createDrawer}
          style={{ marginLeft: "auto", marginRight: "12px" }}
        >
          {_("Add Drawer")}
        </button>
        <button disabled={saving} className="controls__ok" onClick={this.save}>
          {saving && (
            <i className="icon-refresh spin" style={{ marginRight: "6px" }} />
          )}
          {saving ? _("Saving...") : _("Save")}
        </button>
      </div>
    );
  }

  renderCreateDrawerForm = () => {
    const { newDrawer, newDrawerError } = this.state;

    if (!newDrawer) {
      return;
    }

    return (
      <div ref={this.formRef} className={styles.section}>
        <div className={styles.sectionContent}>
          <div className={styles.sectionLogo}>
            <svg viewBox="0 0 20 18" fill="none">
              <path
                d="M17.6372 2.11531C17.337 1.28089 16.5461 0.724164 15.6593 0.722687H4.3407C3.45244 0.724492 2.66085 1.28302 2.36131 2.11909L0.0231421 8.85918C0.00787815 8.90333 0 8.94994 0 8.99688V15.2045C0.00131303 16.3643 0.941111 17.3041 2.10084 17.3054H17.8992C19.0589 17.3041 19.9987 16.3643 20 15.2045V8.95486C20 8.90759 19.9921 8.86082 19.9765 8.81617L17.6372 2.11531ZM3.15405 2.3986C3.3341 1.89801 3.80876 1.56384 4.3407 1.56302H15.6593C16.1903 1.56352 16.6641 1.8962 16.845 2.39548L19.0044 8.58131H16.4519V6.39971C16.4519 6.16764 16.2638 5.97955 16.0317 5.97955H15.2692V3.56112C15.2692 3.32904 15.0811 3.14095 14.849 3.14095H5.38751C5.15543 3.14095 4.96734 3.32904 4.96734 3.56112V5.97955H3.96829C3.73621 5.97955 3.54812 6.16764 3.54812 6.39971V8.58131H1.00906L3.15405 2.3986ZM14.429 5.97955H5.80767V3.98129H14.4288V5.97955H14.429ZM15.6117 6.81988V8.58131H14.1395C13.9397 8.5818 13.7677 8.72246 13.7277 8.9181C13.5877 9.485 13.3435 10.021 13.0073 10.4985C12.2947 11.5023 11.2909 12.0111 10.0238 12.0111C8.75657 12.0111 7.75276 11.5023 7.04028 10.4985C6.70431 10.0212 6.46008 9.48565 6.31992 8.91908C6.28069 8.72262 6.10819 8.58131 5.90796 8.58131H4.38846V6.81988H15.6117ZM19.1597 15.2045C19.1588 15.9003 18.5951 16.4642 17.8992 16.465H2.10084C1.40494 16.4642 0.841157 15.9003 0.840336 15.2045V9.42164H5.58594C5.75647 9.96852 6.00922 10.4863 6.33535 10.9572C6.94049 11.8214 8.07117 12.8514 10.0236 12.8514C11.9761 12.8514 13.1071 11.8214 13.7119 10.9572C14.0382 10.4863 14.2908 9.96852 14.4613 9.42164H19.1597V15.2045Z"
                fill="#18A0FB"
              />
            </svg>
          </div>
          <div className={styles.sectionBody}>
            <input
              style={{
                marginBottom: "8px",
                fontSize: "24px",
                lineHeight: "28px",
                color: "#29394D",
                fontWeight: 500,
                fontStyle: "normal"
              }}
              type="text"
              value={newDrawer.label}
              onChange={({ target }) =>
                this.setState({
                  newDrawer: { ...newDrawer, label: target.value }
                })
              }
              placeholder={_("Enter the name of your drawer ...")}
            />
            <input
              type="text"
              value={newDrawer.url}
              onChange={({ target }) =>
                this.setState({
                  newDrawer: { ...newDrawer, url: target.value }
                })
              }
              placeholder={_("Enter the URL of your drawer ...")}
            />

            {newDrawerError ? (
              <span className={styles.error}>{_(newDrawerError)}</span>
            ) : null}
          </div>

          <button className={styles.sectionButton} onClick={this.addNewDrawer}>
            {_("Save")}
          </button>
        </div>
      </div>
    );
  };

  renderAppSection(app) {
    const { drawers, invalidDrawers } = this.state;
    const { navCommunity } = this.props;
    const { appsState } = navCommunity;

    const drawerIndex = drawers.findIndex(d => d.app === app);
    const drawer = drawers[drawerIndex];
    const appState = appsState.find(
      a => a.code.toLowerCase() === app.toLowerCase()
    );

    if (!drawer || !appState || !appState.activated) {
      return null;
    }

    const logo = appState.icon;
    const isInvalid = invalidDrawers.includes(drawerIndex);

    return (
      <div
        id={`${app}-DRAWER`}
        key={`${app}-DRAWER`}
        className={
          drawer.enabled
            ? styles.section
            : `${styles.section} ${styles.sectionDisabled}`
        }
      >
        <div className={styles.sectionContent}>
          <div className={styles.sectionLogo}>
            <img src={logo} alt={drawer.label} />
          </div>
          <div className={styles.sectionBody}>
            <span>{drawer.label}</span>
            <input
              type="text"
              value={drawer.url}
              onChange={e => this.handleUrlChange(e, drawerIndex)}
            />
            {isInvalid ? (
              <span className={styles.error}>{_("Invalid URL")}</span>
            ) : null}
          </div>

          <button
            className={
              drawer.enabled
                ? `${styles.sectionButton} ${styles.sectionButtonDisable}`
                : styles.sectionButton
            }
            onClick={() => this.handleEnabledChange(drawerIndex)}
          >
            {drawer.enabled ? _("Disable") : _("Enable")}
          </button>
        </div>
      </div>
    );
  }

  renderCustomDrawers = () => {
    const { drawers, invalidDrawers } = this.state;
    // const customDrawers = drawers.find(d => d.app === "OTHER");

    // if (customDrawers.length === 0) {
    //   return null;
    // }

    return drawers.map((drawer, drawerIndex) => {
      if (drawer.app !== "OTHER") {
        return null;
      }

      const isInvalid = invalidDrawers.includes(drawerIndex);

      return (
        <div
          key={`OTHER-DRAWER-${drawerIndex}-${drawer.label}`}
          className={styles.section}
        >
          <div className={styles.sectionContent}>
            <div className={styles.sectionLogo}>
              <svg viewBox="0 0 20 18" fill="none">
                <path
                  d="M17.6372 2.11531C17.337 1.28089 16.5461 0.724164 15.6593 0.722687H4.3407C3.45244 0.724492 2.66085 1.28302 2.36131 2.11909L0.0231421 8.85918C0.00787815 8.90333 0 8.94994 0 8.99688V15.2045C0.00131303 16.3643 0.941111 17.3041 2.10084 17.3054H17.8992C19.0589 17.3041 19.9987 16.3643 20 15.2045V8.95486C20 8.90759 19.9921 8.86082 19.9765 8.81617L17.6372 2.11531ZM3.15405 2.3986C3.3341 1.89801 3.80876 1.56384 4.3407 1.56302H15.6593C16.1903 1.56352 16.6641 1.8962 16.845 2.39548L19.0044 8.58131H16.4519V6.39971C16.4519 6.16764 16.2638 5.97955 16.0317 5.97955H15.2692V3.56112C15.2692 3.32904 15.0811 3.14095 14.849 3.14095H5.38751C5.15543 3.14095 4.96734 3.32904 4.96734 3.56112V5.97955H3.96829C3.73621 5.97955 3.54812 6.16764 3.54812 6.39971V8.58131H1.00906L3.15405 2.3986ZM14.429 5.97955H5.80767V3.98129H14.4288V5.97955H14.429ZM15.6117 6.81988V8.58131H14.1395C13.9397 8.5818 13.7677 8.72246 13.7277 8.9181C13.5877 9.485 13.3435 10.021 13.0073 10.4985C12.2947 11.5023 11.2909 12.0111 10.0238 12.0111C8.75657 12.0111 7.75276 11.5023 7.04028 10.4985C6.70431 10.0212 6.46008 9.48565 6.31992 8.91908C6.28069 8.72262 6.10819 8.58131 5.90796 8.58131H4.38846V6.81988H15.6117ZM19.1597 15.2045C19.1588 15.9003 18.5951 16.4642 17.8992 16.465H2.10084C1.40494 16.4642 0.841157 15.9003 0.840336 15.2045V9.42164H5.58594C5.75647 9.96852 6.00922 10.4863 6.33535 10.9572C6.94049 11.8214 8.07117 12.8514 10.0236 12.8514C11.9761 12.8514 13.1071 11.8214 13.7119 10.9572C14.0382 10.4863 14.2908 9.96852 14.4613 9.42164H19.1597V15.2045Z"
                  fill="#18A0FB"
                />
              </svg>
            </div>
            <div className={styles.sectionBody}>
              <span>{drawer.label}</span>
              <input
                type="text"
                value={drawer.url}
                onChange={e => this.handleUrlChange(e, drawerIndex)}
              />

              {isInvalid ? (
                <span className={styles.error}>{_("Invalid URL")}</span>
              ) : null}
            </div>

            <button
              className={`${styles.sectionButton} ${
                styles.sectionButtonDisable
              }`}
              onClick={() => this.deleteDrawer(drawerIndex)}
            >
              {_("Delete")}
            </button>
          </div>
        </div>
      );
    });
  };

  render() {
    const { loggedAsPost, currentPortal } = this.props;

    let error = null;

    if (!["LEGAL_REPRESENTATIVE", "MANAGER"].includes(loggedAsPost)) {
      error = _("You don't have enough permissions to see this page");
    }

    if (currentPortal !== "COMMUNITY") {
      error = _("This page is accessible only in 'Community' mode");
    }

    if (error) {
      return (
        <div
          id="main"
          className="page manage-drawers"
          style={{
            paddingTop: "62px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {error}
        </div>
      );
    }

    return (
      <div
        id="main"
        className="page manage-drawers"
        style={{ paddingTop: "62px" }}
      >
        {this.renderHeader()}
        {this.renderActions({ border: true })}
        {["BLOG", "EVENT", "FORUM", "MEDIA"].map(app => {
          return this.renderAppSection(app);
        })}
        {this.renderCustomDrawers()}
        {this.renderCreateDrawerForm()}
        {this.renderActions()}
      </div>
    );
  }
}

// Connected Component
const mapStateToProps = state => ({
  routing: selectRouting(state),
  navCommunity: state.auth.navCommunity,
  loggedAsPost: state.auth.loggedAsPost,
  currentPortal: state.auth.currentPortal,
  saving: state.auth.savingCommunityDrawersSettings,
  savingError: state.auth.savingCommunityDrawersSettingsError
});

const mapDispatchToProps = dispatch => ({
  saveCommunityDrawersSettings: _ => dispatch(saveCommunityDrawersSettings(_)),
  displayFlashMessage: (status, message) =>
    dispatch(displayFlashMessage(status, message))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageDrawers);



// WEBPACK FOOTER //
// ./src/components/Settings/ManageDrawers/ManageDrawers.js